<template>
  <div class="card-header">
    <div class="header-top">
      <div class="resource_icon">
        <i class="bi bi-hdd-fill" />
      </div>
      <div
        v-if="resourceDataFormats.length && resource.dataformat && resource.dataformat.id"
        class="resource_format"
      >
        {{
          resourceDataFormats.length && resource.dataformat && resource.dataformat.id ?
            resourceDataFormats.find(el => el.id === resource.dataformat.id).codename :
            ''
        }}
      </div>
      <div class="resource_name">
        <div
          v-for="filePath of resource.href"
          :key="filePath"
        >
          {{ filePath.split('/').slice(-1)[0] }} 
          <i v-if="resource && resource.file">({{ fileSize }})</i>
        </div>
      </div>
    </div>
    <div class="header-bottom form-row">
      <slot name="publication" />
      <b-button
        v-if="userData.is_superuser"
        variant="outline-secondary"
        @click="isUpdateFTPModalOpen = true"
      >
        {{ $t('buttons.edit') }}
      </b-button>
    </div>
    <div
      v-if="isMain"
      class="form-row"
    >
      <label style="margin-top: 1em;">
        {{ $t('resource.scheduler.label') }}
      </label>
      <p>
        {{ $t('resource.scheduler.help') }}
      </p>
      <b-overlay
        :show="loadingScheduler"
        rounded="lg"
        opacity="0.6"
        style="width: calc(100% - 0.5rem);"
      >
        <template #overlay>
          <div class="d-flex align-items-center">
            <b-spinner
              small
              type="grow"
              variant="secondary"
            />
            <b-spinner
              type="grow"
              variant="dark"
            />
            <b-spinner
              small
              type="grow"
              variant="secondary"
            />
            <span class="sr-only">Please wait...</span>
          </div>
        </template>
        <Multiselect
          v-model="scheduler"
          :options="schedulers"
          :disabled="!selectedScenario"
          track-by="id"
          label="label"
          select-label=""
          selected-label=""
          deselect-label=""
          :searchable="false"
          :placeholder="$t('resource.scheduler.placeholder')"
          @select="setScheduler"
          @remove="removeScheduler"
        />
      </b-overlay>
    </div>
    <div
      v-if="isMain"
      class="form-row"
    >
      <label style="margin-top: 1em;">
        {{ $t('resource.scenario.label') }}
      </label>
      <p>
        {{ $t('resource.scenario.help') }}
      </p>
      <b-overlay
        :show="loadingScheduler"
        rounded="lg"
        opacity="0.6"
        style="width: calc(100% - 0.5rem);"
      >
        <template #overlay>
          <div class="d-flex align-items-center">
            <b-spinner
              small
              type="grow"
              variant="secondary"
            />
            <b-spinner
              type="grow"
              variant="dark"
            />
            <b-spinner
              small
              type="grow"
              variant="secondary"
            />
            <span class="sr-only">Please wait...</span>
          </div>
        </template>
        <Multiselect
          v-model="selectedScenario"
          :options="scenarios"
          :disabled="scenarios.length < 2"
          track-by="id"
          label="label"
          select-label=""
          selected-label=""
          deselect-label=""
          :searchable="false"
          :placeholder="$t('resource.scenario.placeholder')"
        />
      </b-overlay>
    </div>
    <UpdateFTPModal
      :open="isUpdateFTPModalOpen"
      :format="resourceDataFormats.find(el => el.id === resource.dataformat ? resource.dataformat.id : 1)"
      @hide="isUpdateFTPModalOpen = false"
      @update="modifyFTP"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { fileConvertSize } from '@/utils';
import {
  ftpAPI,
  resourceAPI
} from '@/api';
import { slugify } from '@/utils';
import { ErrorService } from '@/services/error-service.js';

import Multiselect from 'vue-multiselect';
import UpdateFTPModal from '@/components/Resource/modals/UpdateFTPModal.vue';

export default {
  name: 'FTPEditor',

  components: {
    Multiselect,
    UpdateFTPModal
  },

  props: {
    isMain: {
      type: Boolean,
      default: true
    },
    resource: {
      type: Object,
      default: () => {}
    },
    metaResource: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      isUpdateFTPModalOpen: false,

      // schedulers
      scheduler: null,
      scenarios: [],
      selectedScenario: null,
      loadingScheduler: false,
      originalScheduler: null // to know if scheduler need to be created or updated
    };
  },

  computed: {
    ...mapState('login', [
      'userData'
    ]),
    ...mapState('resources', [
      'resourceDataFormats',
      'schedulers'
    ]),
    ...mapState('datasets', [
      'currentDataset'
    ]),

    fileSize() {
      return fileConvertSize(this.resource.file.size);
    },
  },

  watch: {
    resource: {
      deep: true,
      async handler(newValue) {
        if (newValue['scenario-execution-frequency']) {
          const response = await resourceAPI.getResourceExecutionFrequency(newValue['scenario-execution-frequency']);
          if (response && response.results && response.results.length) {
            this.scheduler = response.results[0].scheduler;
            this.originalScheduler = response.results[0];
            if (newValue._scenarios && newValue._scenarios.update && newValue._scenarios.update.length) {
              this.scenarios = newValue._scenarios.update;
              if (this.scenarios.length) {
                if (
                  this.originalScheduler &&
                  this.originalScheduler.core_scenario &&
                  this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename)
                ) {
                  this.selectedScenario =
                    this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename);
                } else {
                  this.selectedScenario = this.scenarios[0];
                }
              }
            }
          }
        } else {
          this.originalScheduler = null;
        }
      }
    }
  },

  async created() {
    if (this.schedulers.length === 0) {
      await this.GET_SCHEDULERS();
    }
    if (this.resource['scenario-execution-frequency']) {
      const response = await resourceAPI.getResourceExecutionFrequency(this.resource['scenario-execution-frequency']);
      if (response && response.results && response.results.length) {
        this.scheduler = response.results[0].scheduler;
        this.originalScheduler = response.results[0];
      }
    }
    if (this.resource._scenarios && this.resource._scenarios.update && this.resource._scenarios.update.length) {
      this.scenarios = this.resource._scenarios.update;
      if (this.scenarios.length) {
        if (
          this.originalScheduler &&
          this.originalScheduler.core_scenario &&
          this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename)
        ) {
          this.selectedScenario = this.scenarios.find(el => el.id === this.originalScheduler.core_scenario.codename);
        } else {
          this.selectedScenario = this.scenarios[0];
        }
      }
    }
  },

  mounted() {
    // Watch modal closing event
    this.$root.$on('bv::modal::hide', () => {
      this.isUpdateFTPModalOpen = false;
    });
  },

  methods: {
    ...mapActions('resources', [
      'GET_SCHEDULERS',
    ]),

    async setScheduler() {
      try {
        if (this.scheduler) {
          this.loadingScheduler = true;
          if (!this.originalScheduler) {
            const schedulerName = `Mise à jour ${this.scheduler.id} - ftp_${this.resource.id}`;
            const schedulerData = {
              codename: slugify(schedulerName),
              display_name: schedulerName,
              core_scenario_id: this.selectedScenario.id,
              scheduler: {
                class_id: this.scheduler.class_id,
                id: this.scheduler.id
              },
              kwargs: {
                dataset_id: this.currentDataset.id,
                force_update_resource_last_update_date: true
              }
            };
            const scheduled = await ftpAPI.setFTPScheduler(
              this.resource.id,
              schedulerData
            );
            if (scheduled) {
              this.originalScheduler = scheduled;
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          } else {
            const schedulerData = {
              core_scenario_id: this.selectedScenario.id,
              scheduler: {
                class_id: this.scheduler.class_id,
                id: this.scheduler.id
              },
              kwargs: {
                dataset_id: this.currentDataset.id,
                force_update_resource_last_update_date: true
              }
            };
            const scheduled = await ftpAPI.updateFTPScheduler(
              this.resource.id,
              schedulerData,
              this.originalScheduler.id
            );
            if (scheduled) {
              this.originalScheduler = scheduled;
              ErrorService.onSuccess(true, this.$t('resource.scheduler.setMessage'));
            }
          }
          this.loadingScheduler = false;
          this.$emit('get');
        }
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    async removeScheduler() {
      try {
        this.loadingScheduler = true;
        const scheduled = await ftpAPI.removeFTPScheduler(
          this.resource.id,
          this.originalScheduler.id
        );
        if (scheduled) {
          this.originalScheduler = null;
          ErrorService.onSuccess(true, this.$t('resource.scheduler.removeMessage'));
        }
        this.loadingScheduler = false;
        this.$emit('get');
      } catch (err) {
        console.error(err);
        this.loadingScheduler = false;
      }
    },

    modifyFTP(e) {
      this.$emit('modify', {
        data: {
          id: this.resource.id,
          ...e.form
        },
        type: this.isMain ? 'resource' : 'annexe',
        resourceType: 'ftp',
        resourceId: this.metaResource.id,
      });
      this.isUpdateFTPModalOpen = false;
    },

    deleteResource() {
      this.$emit('delete');
    },
  }
};
</script>